import { useEffect, useState } from "react";

import {
  Badge,
  CheckCircleFilledSVG,
  CloseCircleFilledSVG,
  Collapse,
} from "@newsela/angelou";

import GuidedHighlightingIcon from "@/icons/GuidedHighlightingIcon";
import RewardIcon from "@/icons/RewardIcon";
import {
  GUIDED_HIGHLIGHTING_SKILLS_META,
  IReadingSkillColor,
} from "@/view/GuidedHighlightingContainer/utils/GuidedHighlighting.constants";
import {
  GUIDED_HIGHLIGHTING_QUESTION_STATUSES,
  IGuidedHighlightingQuestion,
} from "@/view/utils/useGuidedHighlightingStudent/types";
import useGuidedHighlightingStudent from "@/view/utils/useGuidedHighlightingStudent/useGuidedHighlightingStudent";
import useGuidedHighlightingStudentAnswer from "@/view/utils/useGuidedHighlightingStudentAnswer";
import CheckCleanButtons from "./CheckCleanButtons";
import CompleteButtons from "./CompleteButtons";
import {
  answerModuleWrapper,
  attemptWrapper,
  badgeCss,
  collapseButton,
  correctRewardWrapper,
  floatingText,
  headerWrappercss,
  highlightedTextStyle,
  highlightedTextStyleValidation,
  iconCss,
  isCorrectIcon,
  isCorrectText,
  isCorrectWrapper,
  moduleHeaderText,
  moduleHeaderWrapper,
  moduleText,
  panelWrapperCss,
  rewardIconCss,
  smallRewardIconCss,
  smallRewardText,
  smallRewardWrapper,
  textCss,
  titleCss,
} from "./GuidedHighlightingStudentSkillQuestion.style";

interface IProps {
  question: IGuidedHighlightingQuestion;
}

const GuidedHighlightingStudentSkillQuestion = ({ question }: IProps) => {
  const {
    activeGuidedHighlightingQuestion,
    isCompleted,
    handleCompleteActivity,
    handleNextQuestion,
    handleActiveQuestion,
    shouldShowIRCTokens,
  } = useGuidedHighlightingStudent();

  const { handleCleanHighlighting, handleAttempt } =
    useGuidedHighlightingStudentAnswer();
  const [resultText, setResultText] = useState("");
  const [highlightedText, setHighlightedText] =
    useState<IReadingSkillColor | null>(null);

  const isHidden = () =>
    activeGuidedHighlightingQuestion?.readingSkill !== question.readingSkill;

  const tokensEarned = question.tokensEarned || 0;
  const tokensEarnedLabel = tokensEarned > 0 ? `+${tokensEarned}` : "0";

  const readingSkillMeta =
    GUIDED_HIGHLIGHTING_SKILLS_META[question.readingSkill];
  const readingSkillName = readingSkillMeta.name;

  const questionColor = readingSkillMeta.color.replace("#", "");
  const questionHighlightIconSrc = `/images/icons/icon_highlight_${questionColor}.svg`;

  const isCardFinished =
    question.status === GUIDED_HIGHLIGHTING_QUESTION_STATUSES.completed;

  useEffect(() => {
    let isResponseIncorrect = false;
    if (activeGuidedHighlightingQuestion) {
      const { selectedText, isCorrect } = activeGuidedHighlightingQuestion;

      setHighlightedText(selectedText ? readingSkillMeta : null);

      isResponseIncorrect = !!selectedText && isCorrect === false;
    }
    setResultText(isResponseIncorrect ? "Incorrect" : "Correct");
  }, [activeGuidedHighlightingQuestion]);

  const handleCheckHighlight = () => {
    handleAttempt();
  };
  const handleTryAgain = () => {
    handleCleanHighlighting();
  };

  const RenderButtons = () => {
    let buttons = null;
    const isOnGoingQuestion =
      activeGuidedHighlightingQuestion?.status !==
      GUIDED_HIGHLIGHTING_QUESTION_STATUSES.initial;
    if (!isCompleted) {
      if (isOnGoingQuestion) {
        buttons = (
          <CompleteButtons
            activeGuidedHighlightingQuestion={activeGuidedHighlightingQuestion}
            handleCompleteActivity={() => handleCompleteActivity()}
            handleNextQuestion={handleNextQuestion}
            handleTryAgain={handleTryAgain}
          />
        );
      } else {
        buttons = (
          <CheckCleanButtons
            activeGuidedHighlightingQuestion={activeGuidedHighlightingQuestion}
            handleCleanHighlighting={handleCleanHighlighting}
            handleCheckHighlight={handleCheckHighlight}
          />
        );
      }
    }

    return buttons;
  };

  const getAttemptLabel = () =>
    activeGuidedHighlightingQuestion?.status !==
      GUIDED_HIGHLIGHTING_QUESTION_STATUSES.initial ||
    (activeGuidedHighlightingQuestion?.attempt || 0) > 0
      ? "1 out of 2 attempts"
      : "1st attempt";
  return question.reported ? null : (
    <Collapse
      data-testid="guided-highlighting-skill-question"
      __cssFor={{
        root: collapseButton,
      }}
      initialIsHidden={isHidden()}
      isHidden={isHidden()}
      renderButtonContents={() => (
        <div
          css={headerWrappercss(readingSkillMeta.color)}
          onClick={() => {
            handleActiveQuestion(question);
          }}
        >
          <Badge
            __cssFor={{
              root: badgeCss(readingSkillMeta.BadgeColor),
            }}
            flavor={Badge.flavor.solid}
            hideIcon
            size={Badge.size.md}
            text={readingSkillName}
          />
          {isCardFinished && (
            <div css={smallRewardWrapper}>
              {shouldShowIRCTokens && (
                <RewardIcon
                  css={smallRewardIconCss}
                  aria-hidden={false}
                  aria-label="Tokens icon"
                />
              )}
              <div css={smallRewardText}>{question.tokensEarned} pts</div>
            </div>
          )}
        </div>
      )}
    >
      <div css={panelWrapperCss}>
        <div css={headerWrappercss(readingSkillMeta.color)}>
          <div>
            <h2 css={titleCss}>{readingSkillName}</h2>
            <p css={textCss}>{question.question}</p>
          </div>
          {isCardFinished && shouldShowIRCTokens ? (
            <div css={correctRewardWrapper}>
              <div css={floatingText}>{tokensEarnedLabel}</div>
              <RewardIcon
                css={rewardIconCss}
                aria-hidden={false}
                aria-label="Tokens icon"
              />
            </div>
          ) : (
            <img
              src={questionHighlightIconSrc}
              css={iconCss}
              alt="Card with pencil"
            />
          )}
        </div>
        <div css={answerModuleWrapper}>
          <div css={moduleHeaderWrapper}>
            <GuidedHighlightingIcon />
            <p css={moduleHeaderText}>You highlighted:</p>
          </div>
          {activeGuidedHighlightingQuestion?.status ===
          GUIDED_HIGHLIGHTING_QUESTION_STATUSES.completed ? (
            <div css={isCorrectWrapper}>
              {activeGuidedHighlightingQuestion?.isCorrect ? (
                <CheckCircleFilledSVG
                  css={isCorrectIcon(
                    activeGuidedHighlightingQuestion?.isCorrect,
                  )}
                />
              ) : (
                <CloseCircleFilledSVG css={isCorrectIcon(false)} />
              )}
              <p css={isCorrectText}>{resultText}</p>
            </div>
          ) : (
            <p css={moduleText}>{getAttemptLabel()}</p>
          )}
          <div css={attemptWrapper}>
            <p
              css={
                activeGuidedHighlightingQuestion?.status ===
                GUIDED_HIGHLIGHTING_QUESTION_STATUSES.tryAgain
                  ? highlightedTextStyleValidation(
                      !!activeGuidedHighlightingQuestion?.isCorrect,
                    )
                  : highlightedTextStyle(highlightedText)
              }
            >
              {activeGuidedHighlightingQuestion?.selectedText ||
                "Highlight the sentence in the text that best reflects the prompt"}
            </p>
          </div>
          {!activeGuidedHighlightingQuestion?.isCorrect &&
            activeGuidedHighlightingQuestion?.status ===
              GUIDED_HIGHLIGHTING_QUESTION_STATUSES.completed && (
              <>
                <div css={isCorrectWrapper}>
                  <CheckCircleFilledSVG css={isCorrectIcon(true)} />
                  <p css={isCorrectText}>Correct!</p>
                </div>
                <div css={attemptWrapper}>
                  <p css={highlightedTextStyleValidation(true)}>
                    {activeGuidedHighlightingQuestion?.suggestedAnswer?.content}
                  </p>
                </div>
              </>
            )}
          <RenderButtons />
        </div>
      </div>
    </Collapse>
  );
};

export default GuidedHighlightingStudentSkillQuestion;
